import { useTheme } from '@/shared/providers/theme';
import { Insurer } from '@/types/admin-insurer';
import { convertWarekiDetail } from '@/utils/wareki';
import { Empty, TableProps } from 'antd';
import { SorterResult } from 'antd/es/table/interface';
import { useNavigate, useParams } from 'react-router-dom';
import { AppTable } from '../atoms';
import AppButton from '../atoms/AppButton';
import { EditFill, EllipsisH } from '../atoms/AppIcon';
import { EMPTY_TEXT_FILTERED } from '@/constants';

interface InsurerTableProps {
  data: Insurer[];
  sortInfo?: {
    columnKey?: string;
    order?: 'ascend' | 'descend';
  };
  onTableChange?: (sorter: SorterResult<Insurer> | SorterResult<Insurer>[]) => void;
  isLoading?: boolean;
}

/**
 * Format ISO date string (YYYY-MM-DD) to Japanese era format: {Gengo}YY年MM月DD日
 */
const formatWarekiDate = (dateStr: string | null): string => {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return '';
  const era = convertWarekiDetail(y, m, d);
  if (!era) return '';
  return `${era}年${String(m).padStart(2, '0')}月${String(d).padStart(2, '0')}日`;
};

/**
 * Format special calculation cost values to display string
 */
export const formatSpecialCalculationCost = (scc: Insurer['special_calculation_cost']): string => {
  if (!scc || scc.calc_type == null) return '';
  const { calc_type, limit_amount, limit_num_month, limit_amount_month } = scc;
  const nf = (v: number | null | undefined) => (v ?? 0).toLocaleString('en-US');
  if (calc_type === 1) {
    return `1回${nf(limit_amount)}円　月${limit_num_month ?? 0}回まで`;
  }
  if (calc_type === 2) {
    return `1ヵ月上限（限度額）${nf(limit_amount_month)}円`;
  }
  if (calc_type === 3) {
    return `1回${nf(limit_amount)}円までで1ヵ月上限（限度額）${nf(limit_amount_month)}円`;
  }
  return '';
};

const InsurerTable = ({ data, sortInfo, onTableChange, isLoading }: InsurerTableProps) => {
  const { colors } = useTheme();
  const { site } = useParams<{ site: string }>();
  const currentSite = site ?? 'medix';
  const navigate = useNavigate();

  const columns: TableProps<Insurer>['columns'] = [
    {
      title: '保険者番号',
      dataIndex: 'insurer_number',
      key: 'insurer_number',
      sorter: true,
      sortOrder: sortInfo?.columnKey === 'insurer_number' ? sortInfo.order : undefined,
      render: (_, record) => (
        <span>
          {record.insurer_number}-{record.sub_number}
        </span>
      ),
      width: '12%',
    },
    {
      title: '保険者名称',
      dataIndex: 'insurer_name',
      key: 'insurer_name',
      sorter: true,
      sortOrder: sortInfo?.columnKey === 'insurer_name' ? sortInfo.order : undefined,
      width: '18%',
    },
    {
      title: '保険種別',
      dataIndex: 'insurer_type',
      key: 'insurer_type',
      sorter: true,
      sortOrder: sortInfo?.columnKey === 'insurer_type' ? sortInfo.order : undefined,
      render: (_, record) => <p>{record.insurer_type.name}</p>,
      width: '8%',
    },
    {
      title: '特殊医療助成',
      key: 'special_calculation_cost',
      render: (_, record) => <p>{formatSpecialCalculationCost(record.special_calculation_cost)}</p>,
      width: '18%',
    },
    {
      title: '請求区分',
      key: 'medix_claim_type',
      dataIndex: 'medix_claim_type',
      render: (_, record) => (
        <p className="whitespace-nowrap">{record.medix_claim_type?.name ?? ''}</p>
      ),
      width: '8%',
    },
    {
      title: '適用期間',
      key: 'available_period',
      width: '18%',
      render: (_, record) => {
        const INDEFINITE_AVAILABLE_TO = '2100-12-31';
        const from = formatWarekiDate(record.available_from);
        const hasEnd =
          !!record.available_to && record.available_to !== INDEFINITE_AVAILABLE_TO;
        if (!from) return null;
        return <p className="whitespace-nowrap">{hasEnd ? `${from} - ${formatWarekiDate(record.available_to)}` : `${from} - `}</p>;
      },
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <div className="flex gap-2">
          <AppButton
            icon={<EllipsisH />}
            type="primary"
            onClick={() => navigate(`/${currentSite}/admin/insurers/${record.id}`)}
          >
            詳細
          </AppButton>
          <AppButton
            style={
              {
                backgroundColor: colors.secondary,
                ['--action-bg-hover' as string]: colors.secondaryHover,
              } as React.CSSProperties
            }
            className="hover:!bg-[var(--action-bg-hover)]"
            icon={<EditFill />}
            type="primary"
            onClick={() => navigate(`/${currentSite}/admin/insurers/${record.id}/edit`)}
          >
            編集
          </AppButton>
          <AppButton
            icon={<EditFill />}
            ghost
            type="primary"
            disabled={!record.available_to || record.available_to === '2100-12-31'}
            onClick={() => navigate(`/${currentSite}/admin/insurers/${record.id}/copy`)}
          >
            複製
          </AppButton>
        </div>
      ),
      width: '18%',
    },
  ];
  return (
    <AppTable
      columns={columns}
      dataSource={data}
      rowKey={record => record.id}
      onChange={(_pagination, _filters, sorter) =>
        onTableChange?.(sorter as SorterResult<Insurer> | SorterResult<Insurer>[])
      }
      loading={isLoading}
      locale={{
        emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={EMPTY_TEXT_FILTERED} />,
      }}
    />
  );
};

export default InsurerTable;
